<template>
  <div class="checkout-step-one">
    <div class="checkout-step__header">
      <div class="row">
        <div class="col-12 col-md-9">
          <h3 class="checkout-step__headline">
            {{ $t('pages.checkoutStepOne.title') }}
          </h3>
          <p class="checkout-step__copy">
            {{ $t('pages.checkoutStepOne.copy') }}
          </p>
          <p class="checkout-step__copy-additional">
            {{ $t('pages.checkoutStepOne.copyAdditional') }}
          </p>
        </div>
      </div>
    </div>

    <div class="checkout-step__content">
      <div class="checkout-step-one__display-selection">
        <div class="row align-items-end">
          <div class="col-md-8">
            <div
              class="display-selection__wrapper"
              :class="getDisplaySelectionWrapperClasses"
              @click="
                setStepStatus({
                  number: 1,
                  key: 'displaySelection',
                  value: 'dates',
                })
              "
            >
              <div class="row no-gutters">
                <div class="col-md-6">
                  <div class="display-selection display-selection--start">
                    <span
                      class="display-selection__label"
                      :class="{ 'display-selection__label--success': dates[0] }"
                    >
                      {{ $t('generic.labels.arrival') }}
                    </span>
                    <span class="display-selection__value">
                      {{ selectedDatesHumanReadable[0] }}
                    </span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="display-selection display-selection--end">
                    <span
                      class="display-selection__label"
                      :class="{ 'display-selection__label--success': dates[1] }"
                    >
                      {{ $t('generic.labels.departure') }}
                    </span>
                    <span class="display-selection__value">
                      {{ selectedDatesHumanReadable[1] }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div
              class="display-selection__wrapper"
              :class="{ active: steps[1].displaySelection === 'persons' }"
              @click="
                setStepStatus({
                  number: 1,
                  key: 'displaySelection',
                  value: 'persons',
                })
              "
            >
              <div class="display-selection display-selection--persons">
                <span class="display-selection__label">
                  {{ $t('generic.labels.guests') }}
                  <iconUsers class="icon"></iconUsers>
                </span>
                <span class="display-selection__value">
                  <iconArrowBlack
                    class="icon"
                    v-if="numGuests === 0"
                  ></iconArrowBlack>
                  <span v-if="numGuests > 0">{{ numGuests }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="checkout-step-one__selection-wrapper white-box"
        :class="getSelectionWrapperClasses"
      >
        <div
          class="checkout-step-one__selection-date"
          v-if="steps[1].displaySelection === 'dates'"
        >
          <ci-datepicker
            v-model="pickedDates"
            :locale="langSlug"
            :config="pickerConfig"
            :jumpDate="jumpDate"
            @dateChanged="onDateChanged($event)"
          ></ci-datepicker>
        </div>
        <div
          class="checkout-step-one__selection-persons"
          v-if="steps[1].displaySelection === 'persons'"
        >
          <div
            class="person-wrapper"
            v-for="person in campground.persons"
            :key="person.id"
          >
            <CiGuest
              :amount="getPersonAmount(person)"
              :guest="person"
              :max="getMaxPerson(person)"
              :min="0"
              @update:amount="checkSetPerson"
            >
            </CiGuest>
          </div>
        </div>
      </div>
    </div>

    <CiProgressNavigation
      :active-step="activeStep"
      :status="status"
      :steps="steps"
    >
    </CiProgressNavigation>
  </div>
</template>

<script>
import { DateTime } from 'luxon'

import CiDatepicker from 'ci-datepicker'
import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconUsers from '@/assets/icons/users.svg'
import { CiGuest, CiProgressNavigation } from '@/components'
import { mapGetters, mapActions } from 'vuex'
import {
  getFormattedDate,
  getJsDate,
  interval,
  intervalContains,
} from '@/utils/date'

export default {
  name: 'CheckoutStepOne',
  components: {
    CiDatepicker,
    CiGuest,
    CiProgressNavigation,
    iconArrowBlack,
    iconUsers,
  },
  data() {
    return {
      jumpDate: null,
      pickedDates: [],
      pickerConfig: {
        dateSelectable: this.dateIsEnabled.bind(this),
        breakpoint: 650,
        displayMonths: 2,
        range: true,
        inline: true,
        minDays: 2,
        minDate: new Date(),
        dateFormat: this.$t('generic.date.format'),
        i18n: this.datepickerI18n(),
      },
      lockTimes: null,
      validTimes: null,
    }
  },
  created() {
    this.trackEcommerceEvents(1)
    if (this.dates[0]) {
      this.$set(this.pickedDates, 0, this.dates[0])
    }

    if (this.dates[1]) {
      this.$set(this.pickedDates, 1, this.dates[1])
    }

    const [validTimes, lockTimes] = this.calculateTimes()

    this.validTimes = validTimes
    this.lockTimes = lockTimes
    if (this.displayedDate) {
      this.jumpDate = getJsDate(this.displayedDate)
    } else if (
      this.pickedDates &&
      this.pickedDates.length > 0 &&
      this.pickedDates[0]
    ) {
      this.jumpDate = getJsDate(this.pickedDates[0])
    } else {
      this.jumpDate = getJsDate(this.firstSelectableDate())
    }
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      steps: 'steps',
      langSlug: 'langSlug',
    }),

    ...mapGetters('booking', {
      dates: 'dates',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
      displayedDate: 'displayedDate',
    }),

    ...mapGetters('campground', {
      availableCampsites: 'availableCampsites',
      campground: 'campground',
      status: 'status',
      periodNotAvailable: 'periodNotAvailable',
    }),

    selectedDatesHumanReadable() {
      return this.getSelectedDatesHumanReadable()
    },

    numGuests() {
      return Object.keys(this.selectedPersons).reduce((previous, key) => {
        return previous + this.selectedPersons[key].amount
      }, 0)
    },

    getSelectionWrapperClasses() {
      return {
        'checkout-step-one__selection-wrapper--dates-1':
          this.steps[1].displaySelection === 'dates' &&
          (!this.dates[0] || this.dates.length === 2),
        'checkout-step-one__selection-wrapper--dates-2':
          this.steps[1].displaySelection === 'dates' && this.dates.length === 1,
        'checkout-step-one__selection-wrapper--persons':
          this.steps[1].displaySelection === 'persons',
      }
    },

    getDisplaySelectionWrapperClasses() {
      return {
        'active display-selection__wrapper--date-1':
          this.steps[1].displaySelection === 'dates' &&
          (!this.dates[0] || this.dates.length === 2),
        'active display-selection__wrapper--date-2':
          this.steps[1].displaySelection === 'dates' && this.dates.length === 1,
      }
    },
  },
  watch: {
    pickedDates(val, oldVal) {
      if (!val) val = []
      if (val.length === 2 && !val[1]) {
        val = [val[0]]
      }

      if (val === oldVal) {
        return
      }

      if (
        val.length === 2 &&
        val[0] &&
        val[1] &&
        val[0] != null &&
        val[1] != null
      ) {
        this.setStepStatus({
          number: 1,
          key: 'displaySelection',
          value: 'persons',
        })
      }

      this.setDates({ dates: val })
      this.pickedDates = val
      this.stepFulfilled()
    },

    dates(val) {
      this.pickedDates = val
    },

    jumpDate(val) {
      this.setDisplayedDate(val)
    },

    numGuests() {
      this.stepFulfilled()
    },
    periodNotAvailable(val) {
      if (val) {
        this.setDates({ dates: [] });
        this.setStepStatus({
          number: 1,
          key: 'displaySelection',
          value: 'dates',
        })
        this.jumpDate = getJsDate(this.firstSelectableDate())
      }
    },
  },
  methods: {
    ...mapActions('application', {
      setApplicationStepStatus: 'setStepStatus',
      trackEcommerceEvents: 'trackEcommerceEvents',
    }),

    ...mapActions('campground', {
      setAvailabilities: 'setAvailabilities',
    }),

    ...mapActions('booking', {
      setDates: 'setDates',
      setDisplayedDate: 'setDisplayedDate',
      setPerson: 'setPerson',
      setSelectedCampsite: 'setSelectedCampsite',
    }),

    datepickerI18n() {
      return {
        nights: [
          this.$tc('generic.date.nights', 1),
          this.$tc('generic.date.nights', 2),
        ],
        until: this.$t('generic.until'),
        today: this.$t('generic.today'),
      }
    },

    calculateTimes() {
      let validTimes = []
      let lockTimes = []

      if (this.campground) {
        for (let season of this.campground.seasons) {
          if (!season.is_lock) {
            for (let timePeriod of season.time_periods) {
              validTimes.push(
                interval(timePeriod.date_start, timePeriod.date_end)
              )
            }
          } else {
            for (let timePeriod of season.time_periods) {
              lockTimes.push(
                interval(timePeriod.date_start, timePeriod.date_end)
              )
            }
          }
        }
      }

      return [validTimes, lockTimes]
    },

    isLockTime(date) {
      for (let lockTime of this.lockTimes) {
        if (intervalContains(lockTime, date)) {
          return true
        }
      }
    },

    dateIsEnabled(date) {
      const yest = DateTime.local()
        .startOf('day')
        .minus({ days: 1 })

      /* istanbul ignore else */
      if (yest >= DateTime.fromJSDate(getJsDate(date)).startOf('day')) {
        return false
      }

      /* istanbul ignore else */
      if (this.isLockTime(date)) {
        return false
      }

      for (let validTime of this.validTimes) {
        /* istanbul ignore else */
        if (intervalContains(validTime, date)) {
          return true
        }
      }

      return false
    },

    resetDates() {
      this.pickedDates = []
    },

    jumpToToday() {
      this.jumpDate = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      )
    },

    firstSelectableDate(validTimes = this.validTimes) {
      const today = DateTime.local().startOf('day')
      let minDate = today.plus({ years: 10 })
      let minDateSet = false

      for (let validTime of validTimes) {
        const s = validTime.start

        /* istanbul ignore else */
        if (intervalContains(validTime, today) && !this.isLockTime(s)) {
          return today.toJSDate()
        }

        /* istanbul ignore else */
        if (s >= today && s <= minDate && !this.isLockTime(s)) {
          minDate = s
          minDateSet = true
        }
      }

      /* istanbul ignore else */
      if (minDateSet) {
        return minDate.toJSDate()
      }

      return null
    },

    onDateChanged(date) {
      this.jumpDate = date
    },

    getSelectedDatesHumanReadable(dates = null) {
      const d = dates ? dates : this.pickedDates
      const format = this.$t('generic.date.format')
      const pleaseSelect = this.$t('generic.date.placeholder')

      /* istanbul ignore else */
      if (d instanceof Date) {
        return [getFormattedDate(d, format), pleaseSelect]
      }
      /* istanbul ignore else */
      if (!d[0]) {
        return [pleaseSelect, pleaseSelect]
      }

      /* istanbul ignore else */
      if (!d[1]) {
        return [getFormattedDate(d[0], format), pleaseSelect]
      }

      return [getFormattedDate(d[0], format), getFormattedDate(d[1], format)]
    },

    stepFulfilled() {
      if (
        this.numGuests > 0 &&
        this.dates.length === 2 &&
        this.dates[0] &&
        this.dates[1]
      ) {
        let hasAdult = false
        this.selectedPersons.forEach(p => {
          if (!hasAdult) {
            const person = this.campground.persons.find(
              pers => pers.id === p.id
            )
            if (person.person_type === 'adult') {
              hasAdult = true
            }
          }
        })
        if (hasAdult) {
          this.setStepStatus({ number: 1, key: 'fulfilled', value: true })
          return
        }
      }

      this.setStepStatus({ number: 1, key: 'fulfilled', value: false })
    },

    getPersonAmount(person) {
      const pers = this.selectedPersons.find(p => p.id === person.id)
      return pers ? pers.amount : 0
    },

    getMaxPerson(person) {
      let amount = 0
      let maxPersons = 0
      let maxPets = 0
      let selectedPersons = 0
      let selectedPets = 0

      this.campground.campsites.forEach(campsite => {
        if (campsite.max_persons + campsite.additional_children > maxPersons)
          maxPersons = campsite.max_persons + campsite.additional_children
        if (campsite.max_pets > maxPets) maxPets = campsite.max_pets
      })

      this.selectedPersons.forEach(sp => {
        if (sp.id === person.id) amount = sp.amount

        const basicPerson = this.campground.persons.find(p => p.id === sp.id)
        if (basicPerson.person_type === 'pet') {
          selectedPets = selectedPets + sp.amount
        }

        if (basicPerson.person_type !== 'pet') {
          selectedPersons = selectedPersons + sp.amount
        }
      })

      const animals = amount + (maxPets - selectedPets)
      const persons = amount + (maxPersons - selectedPersons)
      return person.person_type === 'pet' ? animals : persons
    },

    checkSetPerson(value) {
      if (this.selectedCampsite) {
        const isSelectedPerson = this.selectedPersons.find(
          p => p.id === value.person.id
        )
        const amount = isSelectedPerson
          ? value.amount - isSelectedPerson.amount
          : value.amount

        if (value.person.person_type !== 'pet') {
          if (
            this.numPersons() + amount >
            this.selectedCampsite.max_persons +
              this.selectedCampsite.additional_children
          ) {
            this.$modal.show('dialog', {
              title: this.$t('modals.CiCampsiteTypeAvailability.title'),
              content: this.$t('modals.CiCampsiteTypeAvailability.content'),
              proceedEvent: /* istanbul ignore next */ () =>
                this.setPersonOnProceed(value),
            })
            return
          }
        } else {
          if (this.numPets() + amount > this.selectedCampsite.max_pets) {
            this.$modal.show('dialog', {
              title: this.$t('modals.CiCampsiteTypeAvailability.title'),
              content: this.$t('modals.CiCampsiteTypeAvailability.content'),
              proceedEvent: /* istanbul ignore next */ () =>
                this.setPersonOnProceed(value),
            })
            return
          }
        }
      }
      this.setPerson(value)
    },

    numPersons() {
      return Object.keys(this.selectedPersons).reduce((previous, key) => {
        const person = this.campground.persons.find(
          p => p.id === this.selectedPersons[key].id
        )
        if (person && person.person_type !== 'pet') {
          return previous + this.selectedPersons[key].amount
        } else {
          return previous
        }
      }, 0)
    },

    numPets() {
      return Object.keys(this.selectedPersons).reduce((previous, key) => {
        const person = this.campground.persons.find(
          p => p.id === this.selectedPersons[key].id
        )
        if (person && person.person_type === 'pet') {
          return previous + this.selectedPersons[key].amount
        } else {
          return previous
        }
      }, 0)
    },

    setPersonOnProceed(value) {
      this.setPerson(value)
      this.setSelectedCampsite(null)
    },

    setStepStatus(params) {
      this.setApplicationStepStatus(params)
    },
  },
}
</script>
<style lang="css">
@import '~ci-datepicker/dist/ci-datepicker.css';
</style>
<style lang="scss" scoped>
.checkout-step-one__header {
  margin: 0 0 30px;
}

.checkout-step-one__headline {
  @include font-format(1.375rem, 1.625em);
}

.checkout-step__copy-additional {
  font-style: italic;
  margin-top: 1rem;
}

.checkout-step-one__display-selection {
  display: block;
  margin: 0 0 30px;
  padding: 0 25px;

  @include media-breakpoint-up(md) {
    margin: 0 0 30px;
    padding: 0;
  }
}

.display-selection {
  @include font('gotham_mediumregular');

  display: flex;
  flex: 1;
  background: rgba(155, 155, 155, 0.1);
  border-radius: 4px;
  align-items: center;
  margin: 0 0 10px;
  height: 55px;

  @include media-breakpoint-up(md) {
    margin: 0;
  }
}

.display-selection__wrapper {
  cursor: pointer;

  &.active {
    &::before {
      border-bottom: solid 22px hsla(0, 0%, 60.8%, 0.5);
      border-left: solid 22px transparent;
      border-right: solid 22px transparent;
      bottom: -1px;
      content: '';
      height: 0;
      position: absolute;
      right: 50%;
      transform: translate(50%, 0);
      width: 0;
      z-index: 10;
    }

    &::after {
      border-bottom: solid 21px white;
      border-left: solid 21px transparent;
      border-right: solid 21px transparent;
      bottom: -1px;
      content: '';
      height: 0;
      position: absolute;
      right: 50%;
      transform: translate(50%, 0);
      width: 0;
      z-index: 10;
    }

    .display-selection {
      height: 60px;
    }

    @include media-breakpoint-up(md) {
      &::before,
      &::after {
        bottom: -31px;
      }
    }
  }

  &.display-selection__wrapper--date-1 {
    &::before {
      bottom: 70px;
    }
    &::after {
      bottom: 70px;
    }

    @include media-breakpoint-up(md) {
      &::before,
      &::after {
        bottom: -31px;
      }
    }
  }

  &.display-selection__wrapper--date-2 {
    &::before {
      bottom: -1px;
    }
    &::after {
      bottom: -1px;
    }

    @include media-breakpoint-up(md) {
      &::before,
      &::after {
        bottom: -31px;
      }
    }
  }
}

.display-selection--end {
  margin-left: 0;
  margin-right: 0;
}

.display-selection__label {
  align-items: center;
  background: $primary-orange;
  border-radius: 4px;
  color: #fff;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 50%;

  &.display-selection__label--success {
    background: $primary-green;
  }

  .icon {
    width: 30px;
    height: auto;
  }
}

.display-selection__value {
  flex: 1;
  text-align: center;
  width: 50%;
}

.display-selection--persons {
  display: flex;

  .display-selection__label {
    flex: 2;
    color: #000;
    background: white;
    border: 1px solid #979797;
    justify-content: space-between;
    padding: 0 15px;
  }

  .display-selection__value {
    flex: 1;
    text-align: center;
    width: 50%;

    .icon {
      transform: rotate(-90deg);
    }
  }
}

.checkout-step-one__selection-persons {
  position: relative;
  margin: 0 0 20px;

  &:last-child {
    margin: 0;
  }
}

.person-wrapper {
  padding: 20px;
}

.checkout-step__content {
  position: relative;
}

.checkout-step-one__selection-wrapper {
  &.checkout-step-one__selection-wrapper--dates-1 {
    margin-top: -166px;
  }

  &.checkout-step-one__selection-wrapper--dates-2 {
    margin-top: -95px;
  }

  &.checkout-step-one__selection-wrapper--persons {
    margin-top: -30px;
  }

  @include media-breakpoint-up(md) {
    position: relative;

    &.checkout-step-one__selection-wrapper--dates-1,
    &.checkout-step-one__selection-wrapper--dates-2,
    &.checkout-step-one__selection-wrapper--persons {
      margin: 0;
    }
  }
}
</style>
